import { Suspense, lazy, useEffect } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { Carregando } from './components/Estado'
import { Layout } from './components/Layout'
import { LoginPage } from './features/auth/LoginPage'
import { DeParasPage } from './features/ingestao/DeParasPage'
import { PendenciasPage } from './features/ingestao/PendenciasPage'
import { UploadDetalhePage } from './features/ingestao/UploadDetalhePage'
import { UploadsPage } from './features/ingestao/UploadsPage'
import { UsuariosPage } from './features/usuarios/UsuariosPage'
import { useSessao } from './stores/sessao'

/**
 * O shopping de preços entra sob demanda.
 *
 * É a maior tela do projeto (Recharts responde por mais da metade do bundle), e
 * quem abre o painel para subir uma planilha ou zerar a fila de pendências não
 * precisa dela. Separar a rota tira esse peso do primeiro carregamento.
 */
const TerraBrasilPage = lazy(() =>
  import('./features/terrabrasil/TerraBrasilPage').then((m) => ({ default: m.TerraBrasilPage })),
)

const FavoritosPage = lazy(() =>
  import('./features/terrabrasil/FavoritosPage').then((m) => ({ default: m.FavoritosPage })),
)

export function App() {
  const verificar = useSessao((estado) => estado.verificar)

  useEffect(() => {
    void verificar()
  }, [verificar])

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<Protegida />}>
          <Route element={<Layout />}>
            <Route index element={<Navigate to="/terra-brasil" replace />} />
            <Route
              path="/terra-brasil"
              element={
                <Suspense fallback={<Carregando rotulo="Carregando o Terra Brasil…" />}>
                  <TerraBrasilPage />
                </Suspense>
              }
            />
            <Route
              path="/favoritos"
              element={
                <Suspense fallback={<Carregando rotulo="Carregando favoritos…" />}>
                  <FavoritosPage />
                </Suspense>
              }
            />
            <Route path="/uploads" element={<UploadsPage />} />
            <Route path="/uploads/:id" element={<UploadDetalhePage />} />
            <Route path="/pendencias" element={<PendenciasPage />} />
            <Route path="/de-paras" element={<DeParasPage />} />
            <Route element={<SoAdmin />}>
              <Route path="/usuarios" element={<UsuariosPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/terra-brasil" replace />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

/**
 * Barreira de rota.
 *
 * Espera a primeira resposta do `/auth/me` antes de decidir. Sem `verificada`,
 * o primeiro render mandaria todo mundo para o login — inclusive quem já tem
 * sessão válida — e a rota digitada na barra de endereço se perderia.
 *
 * Isto é conveniência de navegação, não segurança: quem manda é o servidor, que
 * recusa cada requisição sem sessão.
 */
function Protegida() {
  const { usuario, verificada } = useSessao()
  const local = useLocation()

  if (!verificada) return <Carregando rotulo="Verificando a sessão…" />
  if (!usuario) {
    return <Navigate to="/login" replace state={{ de: local.pathname + local.search }} />
  }
  return <Outlet />
}

/**
 * Barreira de admin — só staff passa.
 *
 * Como `Protegida`, é conveniência de navegação: quem não é staff é mandado de
 * volta em vez de ver uma tela que só devolveria 403. A recusa de verdade é do
 * backend (`IsAdminUser`).
 */
function SoAdmin() {
  const usuario = useSessao((estado) => estado.usuario)
  return usuario?.isStaff ? <Outlet /> : <Navigate to="/terra-brasil" replace />
}
